/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper";
import { SqlVulnerabilityAssessmentRuleBaseline } from "../operationsInterfaces";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers";
import * as Parameters from "../models/parameters";
import { SqlManagementClient } from "../sqlManagementClient";
import {
  DatabaseSqlVulnerabilityAssessmentRuleBaseline,
  VulnerabilityAssessmentName,
  BaselineName,
  SqlVulnerabilityAssessmentRuleBaselineListByBaselineNextOptionalParams,
  SqlVulnerabilityAssessmentRuleBaselineListByBaselineOptionalParams,
  SqlVulnerabilityAssessmentRuleBaselineListByBaselineResponse,
  SqlVulnerabilityAssessmentRuleBaselineGetOptionalParams,
  SqlVulnerabilityAssessmentRuleBaselineGetResponse,
  DatabaseSqlVulnerabilityAssessmentRuleBaselineInput,
  SqlVulnerabilityAssessmentRuleBaselineCreateOrUpdateOptionalParams,
  SqlVulnerabilityAssessmentRuleBaselineCreateOrUpdateResponse,
  SqlVulnerabilityAssessmentRuleBaselineListByBaselineNextResponse
} from "../models";

/// <reference lib="esnext.asynciterable" />
/** Class containing SqlVulnerabilityAssessmentRuleBaseline operations. */
export class SqlVulnerabilityAssessmentRuleBaselineImpl
  implements SqlVulnerabilityAssessmentRuleBaseline {
  private readonly client: SqlManagementClient;

  /**
   * Initialize a new instance of the class SqlVulnerabilityAssessmentRuleBaseline class.
   * @param client Reference to the service client
   */
  constructor(client: SqlManagementClient) {
    this.client = client;
  }

  /**
   * Gets a system database's sql vulnerability assessment rule baseline.
   * @param resourceGroupName The name of the resource group that contains the resource. You can obtain
   *                          this value from the Azure Resource Manager API or the portal.
   * @param serverName The name of the server.
   * @param vulnerabilityAssessmentName The name of the vulnerability assessment.
   * @param baselineName
   * @param options The options parameters.
   */
  public listByBaseline(
    resourceGroupName: string,
    serverName: string,
    vulnerabilityAssessmentName: VulnerabilityAssessmentName,
    baselineName: BaselineName,
    options?: SqlVulnerabilityAssessmentRuleBaselineListByBaselineOptionalParams
  ): PagedAsyncIterableIterator<
    DatabaseSqlVulnerabilityAssessmentRuleBaseline
  > {
    const iter = this.listByBaselinePagingAll(
      resourceGroupName,
      serverName,
      vulnerabilityAssessmentName,
      baselineName,
      options
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listByBaselinePagingPage(
          resourceGroupName,
          serverName,
          vulnerabilityAssessmentName,
          baselineName,
          options,
          settings
        );
      }
    };
  }

  private async *listByBaselinePagingPage(
    resourceGroupName: string,
    serverName: string,
    vulnerabilityAssessmentName: VulnerabilityAssessmentName,
    baselineName: BaselineName,
    options?: SqlVulnerabilityAssessmentRuleBaselineListByBaselineOptionalParams,
    settings?: PageSettings
  ): AsyncIterableIterator<DatabaseSqlVulnerabilityAssessmentRuleBaseline[]> {
    let result: SqlVulnerabilityAssessmentRuleBaselineListByBaselineResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listByBaseline(
        resourceGroupName,
        serverName,
        vulnerabilityAssessmentName,
        baselineName,
        options
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listByBaselineNext(
        resourceGroupName,
        serverName,
        vulnerabilityAssessmentName,
        baselineName,
        continuationToken,
        options
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listByBaselinePagingAll(
    resourceGroupName: string,
    serverName: string,
    vulnerabilityAssessmentName: VulnerabilityAssessmentName,
    baselineName: BaselineName,
    options?: SqlVulnerabilityAssessmentRuleBaselineListByBaselineOptionalParams
  ): AsyncIterableIterator<DatabaseSqlVulnerabilityAssessmentRuleBaseline> {
    for await (const page of this.listByBaselinePagingPage(
      resourceGroupName,
      serverName,
      vulnerabilityAssessmentName,
      baselineName,
      options
    )) {
      yield* page;
    }
  }

  /**
   * Gets a system database's sql vulnerability assessment rule baseline.
   * @param resourceGroupName The name of the resource group that contains the resource. You can obtain
   *                          this value from the Azure Resource Manager API or the portal.
   * @param serverName The name of the server.
   * @param vulnerabilityAssessmentName The name of the vulnerability assessment.
   * @param baselineName
   * @param options The options parameters.
   */
  private _listByBaseline(
    resourceGroupName: string,
    serverName: string,
    vulnerabilityAssessmentName: VulnerabilityAssessmentName,
    baselineName: BaselineName,
    options?: SqlVulnerabilityAssessmentRuleBaselineListByBaselineOptionalParams
  ): Promise<SqlVulnerabilityAssessmentRuleBaselineListByBaselineResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        serverName,
        vulnerabilityAssessmentName,
        baselineName,
        options
      },
      listByBaselineOperationSpec
    );
  }

  /**
   * Gets a system database's sql vulnerability assessment rule baseline.
   * @param resourceGroupName The name of the resource group that contains the resource. You can obtain
   *                          this value from the Azure Resource Manager API or the portal.
   * @param serverName The name of the server.
   * @param vulnerabilityAssessmentName The name of the vulnerability assessment.
   * @param baselineName
   * @param ruleId The vulnerability assessment rule ID.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    serverName: string,
    vulnerabilityAssessmentName: VulnerabilityAssessmentName,
    baselineName: BaselineName,
    ruleId: string,
    options?: SqlVulnerabilityAssessmentRuleBaselineGetOptionalParams
  ): Promise<SqlVulnerabilityAssessmentRuleBaselineGetResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        serverName,
        vulnerabilityAssessmentName,
        baselineName,
        ruleId,
        options
      },
      getOperationSpec
    );
  }

  /**
   * Creates or updates a database's vulnerability assessment rule baseline.
   * @param resourceGroupName The name of the resource group that contains the resource. You can obtain
   *                          this value from the Azure Resource Manager API or the portal.
   * @param serverName The name of the server.
   * @param vulnerabilityAssessmentName The name of the vulnerability assessment.
   * @param baselineName
   * @param ruleId The vulnerability assessment rule ID.
   * @param parameters The requested rule baseline resource.
   * @param options The options parameters.
   */
  createOrUpdate(
    resourceGroupName: string,
    serverName: string,
    vulnerabilityAssessmentName: VulnerabilityAssessmentName,
    baselineName: BaselineName,
    ruleId: string,
    parameters: DatabaseSqlVulnerabilityAssessmentRuleBaselineInput,
    options?: SqlVulnerabilityAssessmentRuleBaselineCreateOrUpdateOptionalParams
  ): Promise<SqlVulnerabilityAssessmentRuleBaselineCreateOrUpdateResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        serverName,
        vulnerabilityAssessmentName,
        baselineName,
        ruleId,
        parameters,
        options
      },
      createOrUpdateOperationSpec
    );
  }

  /**
   * ListByBaselineNext
   * @param resourceGroupName The name of the resource group that contains the resource. You can obtain
   *                          this value from the Azure Resource Manager API or the portal.
   * @param serverName The name of the server.
   * @param vulnerabilityAssessmentName The name of the vulnerability assessment.
   * @param baselineName
   * @param nextLink The nextLink from the previous successful call to the ListByBaseline method.
   * @param options The options parameters.
   */
  private _listByBaselineNext(
    resourceGroupName: string,
    serverName: string,
    vulnerabilityAssessmentName: VulnerabilityAssessmentName,
    baselineName: BaselineName,
    nextLink: string,
    options?: SqlVulnerabilityAssessmentRuleBaselineListByBaselineNextOptionalParams
  ): Promise<SqlVulnerabilityAssessmentRuleBaselineListByBaselineNextResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        serverName,
        vulnerabilityAssessmentName,
        baselineName,
        nextLink,
        options
      },
      listByBaselineNextOperationSpec
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listByBaselineOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Sql/servers/{serverName}/sqlVulnerabilityAssessments/{vulnerabilityAssessmentName}/baselines/{baselineName}/rules",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper:
        Mappers.DatabaseSqlVulnerabilityAssessmentRuleBaselineListResult
    },
    default: {}
  },
  queryParameters: [Parameters.apiVersion4, Parameters.systemDatabaseName],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.serverName,
    Parameters.vulnerabilityAssessmentName,
    Parameters.baselineName1
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const getOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Sql/servers/{serverName}/sqlVulnerabilityAssessments/{vulnerabilityAssessmentName}/baselines/{baselineName}/rules/{ruleId}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DatabaseSqlVulnerabilityAssessmentRuleBaseline
    },
    default: {}
  },
  queryParameters: [Parameters.apiVersion4, Parameters.systemDatabaseName],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.serverName,
    Parameters.vulnerabilityAssessmentName,
    Parameters.ruleId,
    Parameters.baselineName1
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const createOrUpdateOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Sql/servers/{serverName}/sqlVulnerabilityAssessments/{vulnerabilityAssessmentName}/baselines/{baselineName}/rules/{ruleId}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.DatabaseSqlVulnerabilityAssessmentRuleBaseline
    },
    default: {}
  },
  requestBody: Parameters.parameters84,
  queryParameters: [Parameters.apiVersion4, Parameters.systemDatabaseName],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.serverName,
    Parameters.vulnerabilityAssessmentName,
    Parameters.ruleId,
    Parameters.baselineName1
  ],
  headerParameters: [Parameters.contentType, Parameters.accept],
  mediaType: "json",
  serializer
};
const listByBaselineNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper:
        Mappers.DatabaseSqlVulnerabilityAssessmentRuleBaselineListResult
    },
    default: {}
  },
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.serverName,
    Parameters.nextLink,
    Parameters.vulnerabilityAssessmentName,
    Parameters.baselineName1
  ],
  headerParameters: [Parameters.accept],
  serializer
};
